import React, { useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useNavigate as useRouterNavigate, generatePath } from 'react-router-dom';

import ActionCard, { ActionCardVariant } from 'components/Common/Cards/ActionCard';
import FinancialDashboardNavigation from 'components/FinancialCheckup/FinancialDashboardNavigation';
import FinancialDashboardFormContainer from 'components/Common/FinancialDashboardFormContainer';
import Skeleton from 'components/Common/Skeleton';
import { ReactComponent as AssignmentIcon } from 'images/assignment-icon.svg';
import { ReactComponent as ChevronIcon } from 'images/chevron.svg';

import { FlowComponentType } from 'routes/types';
import { getLoanBreakdown as getLoanBreakdownThunk } from 'thunks';
import { getApplicationData } from 'selectors/getApplicationData';
import { getLoanBreakdown } from 'selectors/getLoanBreakdown';
import { formatMonetaryAmount } from 'utils/formatMonetaryAmount';
import { formatShortDate } from 'utils/dateUtils';
import { RoutePath } from 'enums/Routes';

import styles from './LoansHistoryPage.module.scss';

const LoansHistoryPage = ({ navigationInfo }: FlowComponentType) => {
  const dispatch = useDispatch();
  const navigate = useRouterNavigate();
  const { application } = useSelector(getApplicationData);
  const { breakdowns, isLoading } = useSelector(getLoanBreakdown);

  useEffect(() => {
    if (application?.id && breakdowns.length === 0 && !isLoading) {
      dispatch(getLoanBreakdownThunk(application.id));
    }
  }, [application]);

  const paidOffLoans = breakdowns.filter((loan) => !loan.nextPaymentDate);
  const totalPaidOffAmount = paidOffLoans.reduce((sum, loan) => sum + loan.totalLoanAmount, 0);

  return (
    <>
      <FinancialDashboardNavigation {...navigationInfo} />
      <FinancialDashboardFormContainer
        title={
          isLoading || breakdowns.length === 0 ? (
            <Skeleton width="180px" height="48px" />
          ) : (
            formatMonetaryAmount(totalPaidOffAmount, true)
          )
        }
        subtitle="Paid off"
      >
        <div className={styles.section}>
          <ActionCard variant={ActionCardVariant.White} contentClassName={styles.actionCardContent}>
            {isLoading || breakdowns.length === 0 ? (
              <div className={styles.skeletonRow}>
                <div className={styles.skeletonItem}>
                  <Skeleton width="30%" height="22px" />
                  <Skeleton width="40%" height="20px" />
                </div>
                <Skeleton width="30%" height="22px" />
              </div>
            ) : (
              <>
                {[...paidOffLoans]
                  .sort((a, b) => new Date(a.finalPaymentDate).getTime() - new Date(b.finalPaymentDate).getTime())
                  .map((loan, index) => (
                    <>
                      {index > 0 && <div className={styles.separator} />}
                      <ActionCard.Row key={loan.id}>
                        <div
                          className={styles.loanRow}
                          onClick={() =>
                            navigate(generatePath(RoutePath.ManageYourLoan, { loanId: loan.id.toString() }))
                          }
                        >
                          <div className={styles.iconContainer}>
                            <AssignmentIcon className={styles.icon} />
                          </div>
                          <div className={styles.textContainer}>
                            <p className={styles.loanTitle}>Loan {index + 1}</p>
                            <p className={styles.loanDescription}>
                              Paid off on {formatShortDate(new Date(loan.finalPaymentDate))}
                            </p>
                          </div>
                          <p className={styles.loanValue}>{formatMonetaryAmount(loan.totalLoanAmount, true)}</p>
                          <div className={styles.chevronContainer}>
                            <ChevronIcon className={styles.chevron} />
                          </div>
                        </div>
                      </ActionCard.Row>
                    </>
                  ))}
              </>
            )}
          </ActionCard>
        </div>
      </FinancialDashboardFormContainer>
    </>
  );
};

export default LoansHistoryPage;
